import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DataTable } from "../../components/DataTable";
import { Button } from "../../components/ui/button";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../lib/api";
import SkeletonTable from "../SkeletonTable";
import { useAuth } from "../../context/AuthContext";
import { DialogDelete } from "../../components/Common/DialogDelete";
import { formatDate, formatEGP } from "../../lib/utils";
import { Avatar } from "antd";
import { SalaryEditSheet } from "./_components/SalaryEditSheet";
import { SalaryAddSheet } from "./_components/SalaryAddSheet";

export default function Salary() {
  const { user, loading } = useAuth();
  // Users
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const handleFetchUsers = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/users`);
      setUsers(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Salaries
  const [loadingSalaries, setLoadingSalaries] = useState(false);
  const [salaries, setSalaries] = useState([]);
  const handleFetchSalary = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/salaries`);
      setSalaries(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingSalaries(false);
    }
  };

  useEffect(() => {
    setLoadingSalaries(true);
    handleFetchSalary();
    setLoadingUsers(true);
    handleFetchUsers();
  }, []);

  const columns = [
    {
      accessorKey: `user.name`,
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          الموظف
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar
            className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300"
            src={`${UrlImage}${row.original.user.image}`}
          >
            {row.original.user.name?.charAt(0)}
          </Avatar>
          <div className="text-right">
            <div className="font-medium">{row.original.user.name}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "created_at",
      header: "تاريخ الدفع",
      cell: ({ row }) =>
        row.original.created_at ? formatDate(row.original.created_at) : "___",
    },
    {
      accessorKey: "value",
      header: "الراتب الأساسي",
      cell: ({ row }) => {
        const basic = parseFloat(row.original.value);
        return formatEGP(basic);
      },
    },
    {
      accessorKey: "total_bonus",
      header: "المكافأة",
      cell: ({ row }) => {
        const bonus = parseFloat(row.original.total_bonus);
        return formatEGP(bonus);
      },
    },
    {
      accessorKey: "total_deduction",
      header: "الخصومات",
      cell: ({ row }) => {
        const deduction = parseFloat(row.original.total_deduction);
        return formatEGP(deduction);
      },
    },
    {
      accessorKey: "net_salary",
      header: "الراتب الصافي",
      cell: ({ row }) => {
        const basic = parseFloat(row.original.value) || 0;
        const bonus = parseFloat(row.original.total_bonus) || 0;
        const deduction = parseFloat(row.original.total_deduction) || 0;
        const net = basic + bonus - deduction;
        return formatEGP(net);
      },
    },
    {
      id: "Actions",
      header: "الإجراءات",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {!loading && user?.role === "admin" && (
            <>
              <SalaryEditSheet
                selectedRecord={row.original}
                handleFetchSalary={handleFetchSalary}
                loadingUsers={loadingUsers}
                users={users}
              />
              <DialogDelete
                title={"الراتب"}
                apiUrl={`/salaries/${row.original.bid}`}
                fetchData={handleFetchSalary}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  const exportData = salaries.map((row) => {
    const basic = parseFloat(row.value) || 0;
    const bonus = parseFloat(row.total_bonus) || 0;
    const deduction = parseFloat(row.total_deduction) || 0;
    const netSalary = basic + bonus - deduction;

    return {
      user: row.user?.name || "",
      email: row.user?.email || "",
      salary_date: row.created_at ? formatDate(row.created_at) : "___",
      basic_salary: formatEGP(basic),
      bonus: formatEGP(bonus),
      deductions: formatEGP(deduction),
      net_salary: formatEGP(netSalary),
    };
  });

  const exportColumns = [
    { accessorKey: "user", header: "الموظف" },
    { accessorKey: "email", header: "البريد الإلكتروني" },
    { accessorKey: "salary_date", header: "تاريخ الدفع" },
    { accessorKey: "basic_salary", header: "الراتب الأساسي" },
    { accessorKey: "bonus", header: "المكافأة" },
    { accessorKey: "deductions", header: "الخصومات" },
    { accessorKey: "net_salary", header: "الراتب الصافي" },
  ];

  const [dateFilter, setDateFilter] = useState(null);

  function filterByCreatedAt(data, date = null) {
    if (!date) {
      return data;
    }

    // return data.filter((item) => {
    //   const createdDate = item.created_at.split("T")[0];
    //   return createdDate === date;
    // });
    return data.filter((item) => item.created_at.includes(date));
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
      className="space-y-5"
    >
      <div className="flex items-center gap-2 justify-between">
        <h1 className="text-2xl font-bold">الرواتب</h1>
        <SalaryAddSheet
          handleFetchSalary={handleFetchSalary}
          loadingUsers={loadingUsers}
          users={users}
        />
      </div>

      {loadingSalaries ? (
        <SkeletonTable />
      ) : (
        <DataTable
          columns={columns}
          data={filterByCreatedAt(salaries, dateFilter)}
          title={"الرواتب"}
          filterNames={["user_name", "user_email"]}
          exportData={exportData}
          exportColumns={exportColumns}
          setDateFilter={setDateFilter}
          useDateFilter={true}
        />
      )}
    </motion.div>
  );
}
